import { SideDrawer } from '../common/side-drawer'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ClaimFormsServiceAPI } from '@API/data-services/claim-forms-service'
import { useState, useCallback, JSX } from 'react'
import { isEqual } from 'lodash'
import { useSelector } from 'react-redux'
import { RootState } from '@AppState/redux-store'
import { Flex, Text, Button, Checkbox, Spinner } from '@radix-ui/themes'
import {
  PersonIcon,
  EnvelopeClosedIcon,
  FileTextIcon,
  ReaderIcon,
  Link1Icon,
  CheckCircledIcon,
  Cross1Icon,
} from '@radix-ui/react-icons'
import { ClaimFormSection } from './claim-form-section'
import { SectionState, BarcodeFormat, BarcodeFormatsConfig } from './claim-form-constants'
import { useInactivityTimer } from './use-inactivity-timer'
import { formatLabel } from './claim-form-utils'
import {
  formatDateToLongMonthDayYearString,
  formatDateToTimeZoneTime,
} from '@Components/util/utils'
import { SectionContent, EditableSectionContent } from './claim-form-fields'
import { DiscardChangesDialog } from './discard-changes-dialog'
import { DEFAULT_STALE_DATA_TIMEOUT } from '@AppState/app-config/types'
import './claim-form-drawer.scss'

type ClaimFormDrawerProps = {
  claimFormId: string
  claimantName: string
  appointmentDate?: string
  onClose: () => void
  onLinkAppointment: (claimFormId: string) => void
  onAuthorized: () => void
}

const SECTION_ICONS: Record<string, JSX.Element> = {
  claimant: <PersonIcon />,
  contact: <EnvelopeClosedIcon />,
  tickets: <FileTextIcon />,
}

const SECTION_TITLES: Record<string, string> = {
  claimant: 'Claimant Information',
  contact: 'Contact Information',
  tickets: 'Ticket Information',
}

const DEFAULT_ICON = <ReaderIcon />

const SECTION_HINTS: Record<string, string> = {
  claimant: 'Click to view claimant name and basic info',
  contact: 'Click to view address and phone info',
  tickets: 'Click to view ticket and claim details',
}

const DEFAULT_HINT = 'Click to view details'

/** Keys that should not be rendered as lockable sections */
const NON_SECTION_KEYS = new Set(['signatureAndDate', 'paymentMethod'])

function validateBarcodeFormat(
  barcode: string,
  gameType: string,
  barcodeFormats: Record<string, BarcodeFormat | BarcodeFormat[]>,
): boolean {
  const rawFmt = barcodeFormats[gameType]
  if (!rawFmt) {
    return true
  }
  const formats = Array.isArray(rawFmt) ? rawFmt : [rawFmt]
  return formats.some(
    (fmt) =>
      (!fmt.placeholder || barcode.length >= fmt.placeholder.length) &&
      (!fmt.pattern || new RegExp(`^${fmt.pattern}$`).test(barcode)),
  )
}

function validateTicket(
  ticket: Record<string, unknown>,
  index: number,
  ticketRequired: string[],
  barcodeFormats: Record<string, BarcodeFormat | BarcodeFormat[]>,
): string | null {
  for (const field of ticketRequired) {
    const val = ticket[field]
    if (!val && val !== false) {
      return `Ticket ${index + 1} is missing a required field: ${formatLabel(field)}`
    }
  }
  const barcode = ticket.barcodeNumber as string | undefined
  if (barcode) {
    const gameType = (ticket.gameType as string | undefined) ?? ''
    if (!validateBarcodeFormat(barcode, gameType, barcodeFormats)) {
      return `Ticket ${index + 1} has an invalid barcode format`
    }
  }
  return null
}

export const ClaimFormDrawer = ({
  claimFormId,
  claimantName,
  appointmentDate,
  onClose,
  onLinkAppointment,
  onAuthorized,
}: ClaimFormDrawerProps) => {
  const [sectionStates, setSectionStates] = useState<Record<string, SectionState>>({})
  const [editingData, setEditingData] = useState<Record<string, Record<string, unknown>>>({})
  const [savingSectionKey, setSavingSectionKey] = useState<string | null>(null)
  const [sectionErrors, setSectionErrors] = useState<Record<string, string>>({})
  const [ticketIndex, setTicketIndex] = useState(0)
  const [pendingDiscard, setPendingDiscard] = useState<string | null>(null)
  const [pendingClose, setPendingClose] = useState(false)
  const [receivedChecked, setReceivedChecked] = useState(false)
  const [authorizedChecked, setAuthorizedChecked] = useState(false)
  const currentUser = useSelector((state: RootState) => state.users.currentUser)
  const customerMetadata = useSelector((state: RootState) => state.users.activeCustomerMetadata)
  const timeoutMs = customerMetadata?.claimFormInactivityTimeoutMs ?? 5 * 60 * 1000
  const queryClient = useQueryClient()

  // --- Queries ---

  const {
    data: claimForm,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['claim-form', claimFormId],
    queryFn: () => ClaimFormsServiceAPI.getClaimFormById(claimFormId),
  })

  const { data: schema } = useQuery({
    queryKey: ['claimFormSchema'],
    queryFn: () => ClaimFormsServiceAPI.getClaimFormSchema(),
    staleTime: DEFAULT_STALE_DATA_TIMEOUT,
  })

  const { data: geoData } = useQuery({
    queryKey: ['claimFormGeoData'],
    queryFn: () => ClaimFormsServiceAPI.getClaimFormGeoData(),
    staleTime: DEFAULT_STALE_DATA_TIMEOUT,
  })

  const formData = claimForm?.formData as Record<string, unknown> | undefined
  const alreadyReceived = !!claimForm?.receivedByAgentName
  const agentName = alreadyReceived ? claimForm.receivedByAgentName : (currentUser?.name ?? '—')
  const alreadyAuthorized = !!claimForm?.authorizedByAgentName
  const authorizedAgentName = alreadyAuthorized
    ? claimForm.authorizedByAgentName
    : (currentUser?.name ?? '—')
  const isSameAgent = currentUser?.authProviderId === claimForm?.receivedByUserId

  const sections = formData
    ? Object.entries(formData).filter(([key]) => !NON_SECTION_KEYS.has(key))
    : []

  // --- Section state management ---

  const getSectionState = (key: string): SectionState => sectionStates[key] ?? SectionState.LOCKED

  const hasUnsavedChanges = useCallback(
    (key: string): boolean => {
      const original = formData?.[key]
      const edited = editingData[key]
      if (edited === undefined) {
        return false
      }
      if (key === 'tickets') {
        const wrapper = edited as unknown as { tickets: unknown; paymentMethod: string }
        return (
          !isEqual(original, wrapper.tickets) ||
          !isEqual((formData?.paymentMethod as string) ?? '', wrapper.paymentMethod)
        )
      }
      return !isEqual(original, edited)
    },
    [editingData, formData],
  )

  const lockAllSections = useCallback(() => {
    const anyEditing = Object.entries(sectionStates).some(
      ([key, state]) => state === SectionState.EDITING && hasUnsavedChanges(key),
    )
    if (anyEditing) {
      const editingKey = Object.entries(sectionStates).find(
        ([key, state]) => state === SectionState.EDITING && hasUnsavedChanges(key),
      )?.[0]
      if (editingKey) {
        setPendingDiscard(editingKey)
      }
      return
    }
    setSectionStates({})
    setEditingData({})
  }, [sectionStates, hasUnsavedChanges])

  useInactivityTimer(lockAllSections, timeoutMs)

  const toggleLock = (key: string) => {
    const current = getSectionState(key)
    if (current === SectionState.EDITING) {
      return
    }
    setSectionStates((prev) => ({
      ...prev,
      [key]: current === SectionState.LOCKED ? SectionState.UNLOCKED : SectionState.LOCKED,
    }))
  }

  const startEditing = (key: string) => {
    if (alreadyReceived) {
      return
    }
    const sectionData = formData?.[key]
    if (sectionData) {
      setEditingData((prev) => ({
        ...prev,
        [key]:
          key === 'tickets'
            ? ({
                tickets: structuredClone(sectionData) as unknown[],
                paymentMethod: (formData?.paymentMethod as string) ?? '',
              } as unknown as Record<string, unknown>)
            : (structuredClone(sectionData) as Record<string, unknown>),
      }))
    }
    setSectionStates((prev) => ({ ...prev, [key]: SectionState.EDITING }))
  }

  const cancelEditing = (key: string) => {
    setEditingData((prev) => {
      const next = { ...prev }
      delete next[key]
      return next
    })
    setSectionErrors((prev) => {
      if (!prev[key]) {
        return prev
      }
      const next = { ...prev }
      delete next[key]
      return next
    })
    setSectionStates((prev) => ({ ...prev, [key]: SectionState.UNLOCKED }))
  }

  const handleCancelEdit = (key: string) => {
    if (hasUnsavedChanges(key)) {
      setPendingDiscard(key)
    } else {
      cancelEditing(key)
    }
  }

  const hasAnyUnsavedChanges = useCallback((): boolean => {
    return Object.entries(sectionStates).some(
      ([key, state]) => state === SectionState.EDITING && hasUnsavedChanges(key),
    )
  }, [sectionStates, hasUnsavedChanges])

  const handleClose = () => {
    if (hasAnyUnsavedChanges()) {
      setPendingClose(true)
    } else {
      onClose()
    }
  }

  const confirmDiscard = () => {
    if (pendingClose) {
      setPendingClose(false)
      onClose()
    } else if (pendingDiscard) {
      cancelEditing(pendingDiscard)
      setPendingDiscard(null)
    }
  }

  const cancelDiscardDialog = () => {
    setPendingDiscard(null)
    setPendingClose(false)
  }

  // --- Mutations ---

  const submitMutation = useMutation({
    mutationFn: () => ClaimFormsServiceAPI.markClaimFormReceived(claimFormId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['claim-form', claimFormId] })
    },
  })

  const authorizeMutation = useMutation({
    mutationFn: () => ClaimFormsServiceAPI.markClaimFormAuthorized(claimFormId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['claim-form', claimFormId] })
      onAuthorized()
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ updatedFormData }: { updatedFormData: Record<string, unknown> }) => {
      return ClaimFormsServiceAPI.updateClaimFormById(claimFormId, updatedFormData)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['claim-form', claimFormId] })
    },
  })

  const isFormReceived = alreadyReceived || submitMutation.isSuccess
  const isFormAuthorized = alreadyAuthorized || authorizeMutation.isSuccess

  const validateTicketsArray = (tickets: unknown[]): string | null => {
    if (!schema) {
      return null
    }
    const sectionSchema = schema?.properties as Record<string, unknown> | undefined
    const ticketRequired = ((
      (sectionSchema?.tickets as Record<string, unknown>)?.items as Record<string, unknown>
    )?.required ?? []) as string[]
    const barcodeFormats = (schema?.barcodeFormats ?? {}) as BarcodeFormatsConfig
    for (let i = 0; i < tickets.length; i++) {
      const error = validateTicket(
        tickets[i] as Record<string, unknown>,
        i,
        ticketRequired,
        barcodeFormats,
      )
      if (error) {
        return error
      }
    }
    return null
  }

  const handleSaveSection = (sectionKey: string) => {
    const sectionData = editingData[sectionKey]
    if (!sectionData) {
      return
    }

    let updatedFormData: Record<string, unknown>
    if (sectionKey === 'tickets') {
      const wrapper = sectionData as unknown as { tickets: unknown[]; paymentMethod: string }
      const error = validateTicketsArray(wrapper.tickets)
      if (error) {
        setSectionErrors((prev) => ({ ...prev, [sectionKey]: error }))
        return
      }
      updatedFormData = {
        ...formData,
        tickets: wrapper.tickets,
        paymentMethod: wrapper.paymentMethod,
      }
    } else {
      updatedFormData = { ...formData, [sectionKey]: sectionData }
    }

    setSavingSectionKey(sectionKey)
    updateMutation.mutate(
      { updatedFormData },
      {
        onSuccess: () => {
          cancelEditing(sectionKey)
          setSavingSectionKey(null)
        },
        onError: () => {
          setSavingSectionKey(null)
        },
      },
    )
  }

  const handleEditingDataChange = (sectionKey: string, updated: Record<string, unknown>) => {
    setEditingData((prev) => ({ ...prev, [sectionKey]: updated }))
    setSectionErrors((prev) => {
      if (!prev[sectionKey]) {
        return prev
      }
      const next = { ...prev }
      delete next[sectionKey]
      return next
    })
  }

  // --- Render ---

  const renderBody = () => {
    if (isLoading) {
      return (
        <Flex justify="center" align="center" py="9">
          <Spinner size="3" />
        </Flex>
      )
    }

    if (isError) {
      return (
        <Flex justify="center" align="center" py="9">
          <Text size="2" color="red">
            Failed to load claim form. Please close and try again.
          </Text>
        </Flex>
      )
    }

    return (
      <Flex direction="column" gap="3" className="claim-form-drawer-body">
        {sections.map(([sectionKey, sectionData]) => (
          <ClaimFormSection
            key={sectionKey}
            title={SECTION_TITLES[sectionKey] ?? formatLabel(sectionKey)}
            icon={SECTION_ICONS[sectionKey] ?? DEFAULT_ICON}
            sectionState={getSectionState(sectionKey)}
            onToggleLock={() => toggleLock(sectionKey)}
            onStartEditing={() => startEditing(sectionKey)}
            onSave={() => handleSaveSection(sectionKey)}
            onCancelEdit={() => handleCancelEdit(sectionKey)}
            isSaving={savingSectionKey === sectionKey}
            saveError={sectionErrors[sectionKey]}
            lockedHint={SECTION_HINTS[sectionKey] ?? DEFAULT_HINT}
            editChildren={
              schema && geoData && !isFormReceived ? (
                <EditableSectionContent
                  sectionKey={sectionKey}
                  data={editingData[sectionKey] ?? (sectionData as Record<string, unknown>)}
                  schema={schema}
                  geoData={geoData}
                  formData={formData}
                  onChange={(updated) =>
                    handleEditingDataChange(sectionKey, updated as Record<string, unknown>)
                  }
                  ticketIndex={sectionKey === 'tickets' ? ticketIndex : undefined}
                  onTicketIndexChange={sectionKey === 'tickets' ? setTicketIndex : undefined}
                />
              ) : undefined
            }>
            <SectionContent
              sectionKey={sectionKey}
              data={sectionData}
              formData={formData}
              ticketIndex={sectionKey === 'tickets' ? ticketIndex : undefined}
              onTicketIndexChange={sectionKey === 'tickets' ? setTicketIndex : undefined}
            />
          </ClaimFormSection>
        ))}

        {claimForm?.claimReservationId ? (
          <>
            <label className="claim-form-received-by">
              <Flex align="center" gap="2">
                <Checkbox
                  color="red"
                  checked={alreadyReceived || receivedChecked}
                  disabled={alreadyReceived}
                  onCheckedChange={(checked) => setReceivedChecked(checked === true)}
                />
                <Text size="2">
                  Claim Received by <Text weight="bold">{agentName ?? '—'}</Text>
                  {claimForm?.receivedAt && (
                    <>
                      {' on '}
                      <Text weight="bold">
                        {formatDateToLongMonthDayYearString(
                          claimForm.receivedAt,
                          customerMetadata?.timezone,
                        )}
                      </Text>
                      {' at '}
                      <Text weight="bold">
                        {formatDateToTimeZoneTime(claimForm.receivedAt, customerMetadata?.timezone)}
                      </Text>
                    </>
                  )}
                </Text>
              </Flex>
            </label>

            {alreadyReceived && (isFormAuthorized || !isSameAgent) ? (
              <label className="claim-form-received-by">
                <Flex align="center" gap="2">
                  <Checkbox
                    color="red"
                    checked={isFormAuthorized || authorizedChecked}
                    disabled={isFormAuthorized}
                    onCheckedChange={(checked) => setAuthorizedChecked(checked === true)}
                  />
                  <Text size="2">
                    Claim Authorized by <Text weight="bold">{authorizedAgentName}</Text>
                    {claimForm?.authorizedAt && (
                      <>
                        {' on '}
                        <Text weight="bold">
                          {formatDateToLongMonthDayYearString(
                            claimForm.authorizedAt,
                            customerMetadata?.timezone,
                          )}
                        </Text>
                        {' at '}
                        <Text weight="bold">
                          {formatDateToTimeZoneTime(
                            claimForm.authorizedAt,
                            customerMetadata?.timezone,
                          )}
                        </Text>
                      </>
                    )}
                  </Text>
                </Flex>
              </label>
            ) : null}

            {isFormReceived ? null : (
              <Button
                size="3"
                className="claim-form-submit-button"
                disabled={
                  alreadyReceived ||
                  !receivedChecked ||
                  submitMutation.isPending ||
                  submitMutation.isSuccess
                }
                onClick={() => submitMutation.mutate()}>
                {submitMutation.isPending ? 'Submitting...' : 'Submit as Received'}
              </Button>
            )}

            {alreadyReceived && !isFormAuthorized ? (
              <Button
                size="3"
                className="claim-form-submit-button"
                disabled={
                  !authorizedChecked || authorizeMutation.isPending || authorizeMutation.isSuccess
                }
                onClick={() => authorizeMutation.mutate()}>
                {authorizeMutation.isPending ? 'Submitting...' : 'Submit Authorization'}
              </Button>
            ) : null}
          </>
        ) : null}
      </Flex>
    )
  }

  return (
    <SideDrawer onClose={handleClose}>
      <div className="claim-form-drawer">
        {isFormAuthorized ? (
          <Flex align="center" justify="between" className="claim-form-authorized-banner">
            <Flex align="center" gap="2">
              <CheckCircledIcon />
              <Text size="2">This form has been reviewed and submitted.</Text>
            </Flex>
            <Cross1Icon className="claim-form-banner-close" onClick={handleClose} />
          </Flex>
        ) : null}
        <Flex direction="column" gap="1" className="claim-form-drawer-header">
          <Flex justify="between" align="start">
            <Flex direction="column" gap="1">
              <Text size="5" weight="bold">
                {claimantName}&apos;s Claim Form
              </Text>
              <Text size="2" color="gray">
                Review each section before submitting the form.
              </Text>
            </Flex>
            {claimForm && !alreadyReceived && (!claimForm.claimReservationId || appointmentDate) ? (
              <Text
                size="2"
                color="red"
                className="claim-form-link-appointment"
                onClick={() => onLinkAppointment(claimForm.id)}>
                {claimForm.claimReservationId ? (
                  <>Appointment Date: {appointmentDate}</>
                ) : (
                  <>
                    <Link1Icon /> Link Existing Appointment
                  </>
                )}
              </Text>
            ) : null}
          </Flex>
        </Flex>

        {renderBody()}
      </div>

      <DiscardChangesDialog
        open={pendingDiscard !== null || pendingClose}
        onDiscard={confirmDiscard}
        onCancel={cancelDiscardDialog}
      />
    </SideDrawer>
  )
}
